import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { formatMinutes, toMinutes } from "../../../lib/tickets";

/** Today in Romania, as the date input wants it. */
function today() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bucharest" }).format(new Date());
}

const BLANK = { date: "", hours: "", minutes: "", note: "" };

/**
 * "Timpul meu" (14.10.2026): the owner's time on a ticket, entry by entry,
 * with the total on top. Only the owner sees it; the server refuses it to
 * anybody else and never sends it.
 */
export default function TicketTime({ ticketId, onChanged }) {
  const [data, setData] = useState({ items: [], total_minutes: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...BLANK, date: today() });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const url = `/admin/tickets/${ticketId}/time`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(url);
      setData(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Timpul nu a putut fi încărcat."));
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const reset = () => { setEditing(null); setForm({ ...BLANK, date: today() }); };

  const save = async (e) => {
    e.preventDefault();
    const minutes = toMinutes(form.hours, form.minutes);
    if (!minutes) { toast.error("Scrie cât timp ai lucrat."); return; }
    setBusy(true);
    try {
      const body = { date: form.date || null, minutes, note: form.note };
      const r = editing ? await api.patch(`${url}/${editing}`, body) : await api.post(url, body);
      setData(r.data);
      toast.success(editing ? "Intrarea a fost corectată." : `Am adăugat ${formatMinutes(minutes)}.`);
      reset();
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Timpul nu a putut fi salvat."));
    } finally {
      setBusy(false);
    }
  };

  const edit = (entry) => {
    setEditing(entry.id);
    setForm({ date: entry.date, hours: String(Math.floor(entry.minutes / 60) || ""),
      minutes: String(entry.minutes % 60 || ""), note: entry.note || "" });
  };

  const remove = async (entry) => {
    if (!window.confirm(`Ștergi intrarea de ${formatMinutes(entry.minutes)} din ${entry.date}?`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`${url}/${entry.id}`);
      setData(r.data);
      toast.success("Intrarea a fost ștearsă.");
      if (editing === entry.id) reset();
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Intrarea nu a putut fi ștearsă."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="ticket-time">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h4 className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold">Timpul meu</h4>
        <div className="text-sm text-[#373A45]">
          Total: <strong className="tabular-nums" data-testid="ticket-time-total">{formatMinutes(data.total_minutes)}</strong>
        </div>
      </div>

      <form onSubmit={save} className="grid grid-cols-2 sm:grid-cols-[9rem_4.5rem_4.5rem_1fr_auto] gap-2 items-end">
        <Field label="Data">
          <input type="date" value={form.date} max={today()} onChange={set("date")} className={INPUT} data-testid="ticket-time-date" />
        </Field>
        <Field label="Ore">
          <input type="number" min="0" max="24" step="0.5" value={form.hours} onChange={set("hours")} className={INPUT}
            inputMode="decimal" data-testid="ticket-time-hours" />
        </Field>
        <Field label="Minute">
          <input type="number" min="0" max="59" value={form.minutes} onChange={set("minutes")} className={INPUT}
            inputMode="numeric" data-testid="ticket-time-minutes" />
        </Field>
        <Field label="Notă (opțional)">
          <input value={form.note} onChange={set("note")} maxLength={200} placeholder="analiză, corectură + teste…"
            className={INPUT} data-testid="ticket-time-note" />
        </Field>
        <div className="flex gap-1.5 col-span-2 sm:col-span-1">
          {editing && (
            <button type="button" onClick={reset} disabled={busy}
              className="rounded-full px-3 py-2 text-xs border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]">
              Renunță
            </button>
          )}
          <button type="submit" disabled={busy}
            className="rounded-full px-3 py-2 text-xs font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] disabled:opacity-60 inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid="ticket-time-save">
            {busy && <Loader2 size={12} className="animate-spin" />} {editing ? "Salvează" : "Adaugă timp"}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="mt-4 text-sm text-[#6B7280]"><Loader2 size={14} className="animate-spin inline" /></div>
      ) : data.items.length === 0 ? (
        <p className="mt-4 text-sm text-[#6B7280]">Încă n-ai trecut timp pe acest tichet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-[#E5E7EB] text-sm" data-testid="ticket-time-list">
          {data.items.map((entry) => (
            <li key={entry.id} className={`flex items-center gap-3 py-2 ${editing === entry.id ? "bg-[#965EA7]/5 -mx-2 px-2 rounded" : ""}`}>
              <span className="text-xs text-[#6B7280] tabular-nums w-24 shrink-0">{entry.date}</span>
              <span className="font-semibold text-[#373A45] tabular-nums w-24 shrink-0">{formatMinutes(entry.minutes)}</span>
              <span className="flex-1 text-[#373A45] truncate">{entry.note || <span className="text-[#9CA3AF]">—</span>}</span>
              <button onClick={() => edit(entry)} disabled={busy} className="text-[#6B7280] hover:text-[#373A45] p-1"
                aria-label="Corectează intrarea" title="Corectează"><Pencil size={14} /></button>
              <button onClick={() => remove(entry)} disabled={busy} className="text-[#6B7280] hover:text-[#9b1f2c] p-1"
                aria-label="Șterge intrarea" title="Șterge"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const INPUT = "w-full px-2.5 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#965EA7] outline-none";

function Field({ label, children }) {
  return (
    <label className="block min-w-0">
      <span className="block text-[11px] font-semibold text-[#373A45] mb-1">{label}</span>
      {children}
    </label>
  );
}
